import { useEffect, useState } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiJson, extractApiErrorMessage } from '@/lib/api';
import { setUserProperties, track } from '@/lib/analytics';
import type {
  ProfessionalOnboardingProfile,
  ProfessionalPracticeTool,
  ProfessionalRole,
} from '@/lib/types';

const FONT =
  "'Nunito', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif";

const ROLE_OPTIONS: { id: ProfessionalRole; label: string }[] = [
  { id: 'attorney', label: 'Family law attorney' },
  { id: 'mediator', label: 'Mediator / parenting coordinator' },
  { id: 'coach', label: 'Divorce coach' },
  { id: 'therapist', label: 'Therapist / counselor' },
  { id: 'other', label: 'Other' },
];

const TOOL_OPTIONS: { id: ProfessionalPracticeTool; label: string }[] = [
  { id: 'clio', label: 'Clio' },
  { id: 'mycase', label: 'MyCase' },
  { id: 'smokeball', label: 'Smokeball' },
  { id: 'practicepanther', label: 'PracticePanther' },
  { id: 'other_pms', label: 'Other practice management software' },
  { id: 'coaching_crm', label: 'Coaching / client CRM (HoneyBook, Dubsado, etc.)' },
  { id: 'spreadsheets', label: 'Spreadsheets or documents only' },
  { id: 'none', label: 'None / not applicable' },
];

type Props = {
  onCompleted: (profile: ProfessionalOnboardingProfile) => void;
};

export function ProOnboardingForm({ onCompleted }: Props) {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();
  const [role, setRole] = useState<ProfessionalRole | null>(null);
  const [roleOther, setRoleOther] = useState('');
  const [tools, setTools] = useState<ProfessionalPracticeTool[]>([]);
  const [toolsOther, setToolsOther] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    track('Pro Onboarding Screen');
  }, []);

  const toggleTool = (tool: ProfessionalPracticeTool) => {
    setTools((prev) => {
      if (tool === 'none') {
        return prev.includes('none') ? [] : ['none'];
      }
      const withoutNone = prev.filter((t) => t !== 'none');
      return withoutNone.includes(tool)
        ? withoutNone.filter((t) => t !== tool)
        : [...withoutNone, tool];
    });
  };

  const saveMutation = useMutation({
    mutationFn: () => {
      if (!role) {
        throw new Error('Please select what best describes your work.');
      }
      if (tools.length === 0) {
        throw new Error('Please select at least one option for tools you use.');
      }
      if (role === 'other' && !roleOther.trim()) {
        throw new Error('Please tell us a bit more about your role.');
      }
      return apiJson<ProfessionalOnboardingProfile>(
        getToken,
        '/v1/professional/onboarding-profile',
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            role,
            role_other: role === 'other' ? roleOther.trim() : null,
            practice_tools: tools,
            practice_tools_other:
              tools.includes('other_pms') || tools.includes('coaching_crm')
                ? toolsOther.trim() || null
                : null,
          }),
        },
      );
    },
    onSuccess: (profile) => {
      track('Pro Onboarding Completed', {
        role: profile.role,
        practice_tools: profile.practice_tools,
        has_role_other: Boolean(profile.role_other),
        has_tools_other: Boolean(profile.practice_tools_other),
      });
      setUserProperties({
        professional_role: profile.role,
        practice_tools: profile.practice_tools,
      });
      queryClient.setQueryData(['professional-onboarding-profile'], profile);
      onCompleted(profile);
    },
    onError: (err) => {
      setError(extractApiErrorMessage(err, 'Could not save. Please try again.'));
    },
  });

  const showToolsOther =
    tools.includes('other_pms') || tools.includes('coaching_crm');

  return (
    <div className='max-w-lg mx-auto px-6 py-10' style={{ fontFamily: FONT }}>
      <h1 className='text-2xl font-bold text-gray-900'>
        A couple quick details
      </h1>
      <p className='mt-2 text-sm text-gray-600'>
        Optional context so we can better support attorneys, mediators, and
        coaches; takes a moment.
      </p>

      <fieldset className='mt-8'>
        <legend className='text-sm font-semibold text-gray-900'>
          What best describes your work?
        </legend>
        <div className='mt-3 space-y-2'>
          {ROLE_OPTIONS.map((option) => {
            const selected = role === option.id;
            return (
              <label
                key={option.id}
                className={`flex items-center gap-3 rounded-xl border px-4 py-3 cursor-pointer transition-colors ${
                  selected
                    ? 'border-[#018A9D] bg-[#018A9D]/5'
                    : 'border-gray-200 bg-white hover:border-gray-300'
                }`}
              >
                <input
                  type='radio'
                  name='professional-role'
                  checked={selected}
                  onChange={() => setRole(option.id)}
                  className='accent-[#018A9D]'
                />
                <span className='text-sm font-medium text-gray-900'>
                  {option.label}
                </span>
              </label>
            );
          })}
        </div>
        {role === 'other' && (
          <input
            type='text'
            value={roleOther}
            onChange={(e) => setRoleOther(e.target.value)}
            placeholder='e.g. Guardian ad litem'
            className='mt-3 w-full rounded-xl border border-gray-200 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#018A9D]/40'
          />
        )}
      </fieldset>

      <fieldset className='mt-8'>
        <legend className='text-sm font-semibold text-gray-900'>
          Which tools do you use to manage clients or cases?
        </legend>
        <p className='mt-1 text-xs text-gray-500'>
          Select all that apply: practice management software, coaching CRMs, or
          none.
        </p>
        <div className='mt-3 space-y-2'>
          {TOOL_OPTIONS.map((option) => {
            const selected = tools.includes(option.id);
            return (
              <label
                key={option.id}
                className={`flex items-center gap-3 rounded-xl border px-4 py-3 cursor-pointer transition-colors ${
                  selected
                    ? 'border-[#018A9D] bg-[#018A9D]/5'
                    : 'border-gray-200 bg-white hover:border-gray-300'
                }`}
              >
                <input
                  type='checkbox'
                  checked={selected}
                  onChange={() => toggleTool(option.id)}
                  className='accent-[#018A9D]'
                />
                <span className='text-sm font-medium text-gray-900'>
                  {option.label}
                </span>
              </label>
            );
          })}
        </div>
        {showToolsOther && (
          <input
            type='text'
            value={toolsOther}
            onChange={(e) => setToolsOther(e.target.value)}
            placeholder='Optional: which tool?'
            className='mt-3 w-full rounded-xl border border-gray-200 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#018A9D]/40'
          />
        )}
      </fieldset>

      {error && (
        <p className='mt-4 text-sm text-red-600' role='alert'>
          {error}
        </p>
      )}

      <button
        type='button'
        disabled={saveMutation.isPending}
        onClick={() => {
          setError(null);
          saveMutation.mutate();
        }}
        className='mt-8 w-full rounded-xl bg-[#018A9D] px-4 py-3 text-sm font-semibold text-white hover:bg-[#017d8d] disabled:opacity-60'
      >
        {saveMutation.isPending ? 'Saving…' : 'Continue'}
      </button>
    </div>
  );
}
